import { ReactElement, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { ThemeContext } from 'styled-components';

import { useCompleteWorkspaceCheckpoint, useUploadAsset, useWorkspace } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	ColorPicker,
	Heading3,
	Separator,
	UndoIcon,
	defaultTheme,
	tokens,
} from '@treams/treams-ui';

import { ImageAspectRatio, ImageShape } from '../../packages/kakapo-ui-kit/types/Image';
import OrganizationThemeService from '../../packages/kakapo-ui-services/services/OrganizationThemeService';
import ImageUploadModalContainer from './ImageUploadModalContainer';

const LineGradientExample = styled(Box)<{ left: string; right: string }>`
	height: 8px;
	width: 100%;
	border-radius: 4px;
	background: linear-gradient(90deg, ${(props) => props.left} 0%, ${(props) => props.right} 100%);
`;

const LogoImage = styled.img`
	max-width: 150px;
	max-height: 150px;
	margin-bottom: 16px;
`;

const LoginPhotoImage = styled.img`
	max-width: 300px;
	max-height: 150px;
	margin-bottom: 16px;
`;

const ImagePlaceholder = styled.div<{ width: number }>`
	width: ${(props) => props.width}px;
	height: 150px;
	border: 2px dotted ${tokens.text.tertiary};
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	text-align: center;
	color: ${tokens.text.secondary};
	justify-content: center;
`;

function LogoSettings(props: { onUpload: () => void }) {
	const { t } = useTranslation();
	const [showUploader, setShowUploader] = useState(false);
	const workspace = useWorkspace();
	const { data } = graphql.useWorkspaceThemeQuery({
		variables: { id: workspace.id },
	});

	const [resetWorkspaceLogo, { loading: isResettingLogo }] =
		graphql.useResetWorkspaceLogoMutation();

	const [updateWorkspaceLogo] = graphql.useUpdateWorkspaceLogoMutation();

	const [uploadLogo, { loading: saving }] = useUploadAsset(async (assetID) => {
		setShowUploader(false);
		await updateWorkspaceLogo({
			variables: {
				input: {
					assetID,
				},
			},
		});

		props.onUpload();
	});

	return (
		<Box>
			<Heading3 mb={2}>{t('settings.theme.logo.title')}</Heading3>

			<Flex flexDirection="column" alignItems="start">
				{data?.workspace.theme.logo ? (
					<LogoImage src={data.workspace.theme.logo.source.url} alt="" />
				) : (
					<ImagePlaceholder width={150}>
						<span>{t('common.noImageSet')}</span>
					</ImagePlaceholder>
				)}
				<Flex flexDirection="row">
					<Button
						type="secondary"
						title={t('settings.theme.updateLogo')}
						disabled={saving || isResettingLogo}
						onClick={() => setShowUploader(true)}
						small
					/>
					{data?.workspace.theme.logo && (
						<Box ml={2}>
							<Button
								type="destructive"
								disabled={saving || isResettingLogo}
								small
								onClick={() => {
									resetWorkspaceLogo();
								}}
								title={t('settings.theme.resetLogo')}
							/>
						</Box>
					)}
				</Flex>
			</Flex>

			{showUploader && (
				<ImageUploadModalContainer
					center
					onSubmit={(blob) => uploadLogo(blob!)}
					imageSpecification={{
						title: t('common.logo'),
						key: 'logo',
						aspectRatio: ImageAspectRatio.Logo,
						shape: ImageShape.Rectangular,
						allowedExtensions: ['image/png', 'image/jpeg'],
					}}
					onCancel={() => setShowUploader(false)}
				/>
			)}
		</Box>
	);
}

function LoginPhotoSettings(props: { onUpload: () => void }) {
	const { t } = useTranslation();
	const [showUploader, setShowUploader] = useState(false);
	const workspace = useWorkspace();
	const { data } = graphql.useWorkspaceThemeQuery({
		variables: { id: workspace.id },
	});

	const [resetWorkspaceBackground, { loading: isResettingBackground }] =
		graphql.useResetWorkspaceBackgroundMutation();

	const [updateWorkspaceBackground] = graphql.useUpdateWorkspaceBackgroundMutation();
	const [uploadBackground, { loading: saving }] = useUploadAsset(async (assetID) => {
		setShowUploader(false);
		await updateWorkspaceBackground({
			variables: {
				input: {
					assetID,
				},
			},
		});

		props.onUpload();
	});

	return (
		<Box>
			<Heading3 mb={2}>{t('settings.theme.loginPhoto.title')}</Heading3>

			<Flex flexDirection="column" alignItems="start">
				{data?.workspace.theme.background ? (
					<LoginPhotoImage src={data.workspace.theme.background.source.url} alt="" />
				) : (
					<ImagePlaceholder width={250}>
						<span>{t('common.noImageSet')}</span>
					</ImagePlaceholder>
				)}
				<Flex flexDirection="row">
					<Button
						type="secondary"
						title={t('settings.theme.updateLoginPhoto')}
						disabled={saving || isResettingBackground}
						small
						onClick={() => setShowUploader(true)}
					/>
					{data?.workspace.theme.background !== null && (
						<Box ml={2}>
							<Button
								type="destructive"
								disabled={saving || isResettingBackground}
								small
								onClick={() => {
									resetWorkspaceBackground();
								}}
								title={t('settings.theme.resetLoginPhoto')}
							/>
						</Box>
					)}
				</Flex>
			</Flex>

			{showUploader && (
				<ImageUploadModalContainer
					center
					onSubmit={(blob) => uploadBackground(blob!)}
					imageSpecification={{
						title: t('common.loginPhoto'),
						key: 'loginPhoto',
						aspectRatio: ImageAspectRatio.Ratio16by9,
						shape: ImageShape.Rectangular,
						minimumWidth: 1440,
						allowedExtensions: ['image/png', 'image/jpeg'],
					}}
					onCancel={() => setShowUploader(false)}
				/>
			)}
		</Box>
	);
}

export default function SettingsThemeContainer(): ReactElement {
	const { t } = useTranslation();
	const theme = useContext(ThemeContext);
	const { colors } = theme;
	const [primaryColor, setPrimaryColor] = useState(colors.primary);
	const lineGradient: string = theme.images.lineGradient ?? defaultTheme.images.lineGradient;
	const lineGradientColors = OrganizationThemeService.parseGradientString(lineGradient);
	const [gradientColors, setGradientColors] = useState(lineGradientColors);
	const [updateColors, { loading: isSavingColors }] = graphql.useUpdateWorkspaceColorsMutation();

	useEffect(() => {
		setPrimaryColor(colors.primary);
		const parsedGradientColors = OrganizationThemeService.parseGradientString(lineGradient);
		setGradientColors(parsedGradientColors);
	}, [theme]);

	const completeCheckpoint = useCompleteWorkspaceCheckpoint('workspace.settings.theme');

	const saveColors = () => {
		updateColors({
			variables: {
				input: {
					primaryColor,
					linearGradientStart: gradientColors[0],
					linearGradientEnd: gradientColors[1],
				},
			},
		});
	};

	const resetColors = () => {
		updateColors({
			variables: {
				input: {
					primaryColor: null,
					primaryColorDark: null,
					secondaryColor: null,
					linearGradientStart: null,
					linearGradientEnd: null,
				},
			},
		});
	};

	return (
		<>
			<Flex flexDirection="row" justifyContent="space-between">
				<LogoSettings
					onUpload={() => {
						completeCheckpoint();
					}}
				/>
				<LoginPhotoSettings
					onUpload={() => {
						completeCheckpoint();
					}}
				/>
			</Flex>
			<Separator mt={4} />
			<Box mt={4}>
				<Flex flexDirection="row" justifyContent="space-between">
					<Box>
						<Heading3>{t('settings.theme.colors.title')}</Heading3>
						<ColorPicker
							label={t('settings.theme.colors.primary.label')}
							color={primaryColor}
							setColor={setPrimaryColor}
						/>
					</Box>
					<Box>
						<Heading3>{t('settings.theme.colors.gradient.title')}</Heading3>
						<Flex justifyContent="space-between" flexDirection="row">
							<Box mr={2}>
								<ColorPicker
									label={t('settings.theme.colors.gradient.left.label')}
									color={gradientColors[0]}
									setColor={(value) => setGradientColors([value, gradientColors[1]])}
								/>
							</Box>
							<ColorPicker
								label={t('settings.theme.colors.gradient.right.label')}
								color={gradientColors[1]}
								setColor={(value) => setGradientColors([gradientColors[0], value])}
							/>
						</Flex>
						<Box mt={3}>
							<LineGradientExample left={gradientColors[0]} right={gradientColors[1]} />
						</Box>
					</Box>
				</Flex>
				<Flex flexDirection="row" mt={3} mb={4}>
					<Button
						type="secondary"
						title={t('settings.theme.updateColors')}
						disabled={isSavingColors}
						small
						onClick={() => saveColors()}
					/>
					<Box ml={2}>
						<Button
							type="secondary"
							icon={<UndoIcon />}
							disabled={isSavingColors}
							small
							onClick={() => resetColors()}
							title={t('settings.theme.resetColors')}
						/>
					</Box>
				</Flex>
			</Box>
		</>
	);
}
